/* Public download / install page for the Teacher & Staff mobile PWA.
 * URL: /m/install (also aliased at /download)
 * No auth required. Show QR + step-by-step per platform (Android / iOS / Desktop).
 */
import { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  DeviceMobile, AndroidLogo, AppleLogo, ShareNetwork,
  DownloadSimple, Check, Copy, ArrowRight, GraduationCap,
} from '@phosphor-icons/react';

const InstallInstructions = ({ platform }) => {
  const steps = {
    android: [
      'Ouvrez ce lien dans Chrome (ou tout navigateur récent).',
      'Une bannière « Ajouter à l\'écran d\'accueil » apparaît en bas.',
      'Sinon : menu (⋮ en haut à droite) → « Installer l\'application » ou « Ajouter à l\'écran d\'accueil ».',
      'Confirmez. L\'icône « Gestion École » apparaît sur votre écran d\'accueil.',
    ],
    ios: [
      'Ouvrez ce lien dans Safari (obligatoire — pas Chrome).',
      'Touchez le bouton Partage (□↑) en bas de l\'écran.',
      'Faites défiler et sélectionnez « Sur l\'écran d\'accueil ».',
      'Confirmez « Ajouter ». L\'app apparaît comme une vraie application.',
    ],
    desktop: [
      'Ouvrez ce lien dans Chrome, Edge ou Brave.',
      'Cliquez sur l\'icône ⊕ dans la barre d\'adresse (à droite de l\'URL).',
      'Confirmez « Installer ». Une fenêtre dédiée s\'ouvre.',
    ],
  }[platform];

  return (
    <ol className="space-y-2.5 text-sm text-[#334155]">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-2.5" data-testid={`install-step-${platform}-${i}`}>
          <span className="h-6 w-6 shrink-0 rounded-full bg-[#0052FF] text-white text-xs font-bold flex items-center justify-center">
            {i + 1}
          </span>
          <span>{s}</span>
        </li>
      ))}
    </ol>
  );
};

const MobileInstall = () => {
  const [platform, setPlatform] = useState(() => {
    const ua = (navigator.userAgent || '').toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    return 'desktop';
  });
  const [copied, setCopied] = useState(false);

  const loginUrl = useMemo(() => `${window.location.origin}/m/login`, []);

  const copy = () => {
    navigator.clipboard.writeText(loginUrl).then(() => {
      setCopied(true);
      toast.success('Lien copié dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Copie impossible'));
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Gestion École — App mobile',
          text: 'Installez l\'app mobile de notre école',
          url: loginUrl,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  const PLATFORMS = [
    { key: 'android', label: 'Android', icon: AndroidLogo, color: '#3DDC84' },
    { key: 'ios', label: 'iPhone / iPad', icon: AppleLogo, color: '#0F172A' },
    { key: 'desktop', label: 'Ordinateur', icon: DeviceMobile, color: '#6366F1' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0052FF] via-[#3B82F6] to-[#10B981]">
      <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6" data-testid="mobile-install-page">
        {/* Hero */}
        <div className="text-center text-white pt-4">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-white/20 backdrop-blur items-center justify-center mb-4">
            <GraduationCap size={36} weight="duotone" />
          </div>
          <h1 className="font-outfit text-3xl md:text-4xl font-bold">
            App mobile Enseignant &amp; Personnel
          </h1>
          <p className="mt-2 opacity-90 text-sm md:text-base max-w-md mx-auto">
            Saisissez les notes, gérez vos disponibilités et confirmez les rendez-vous parents
            depuis votre téléphone.
          </p>
        </div>

        {/* QR + Link card */}
        <Card className="border-0 shadow-2xl" data-testid="install-qr-card">
          <CardContent className="p-6 grid md:grid-cols-2 gap-6 items-center">
            <div className="flex flex-col items-center gap-3">
              <div className="bg-white border-2 border-dashed border-[#0052FF] rounded-lg p-4">
                <QRCodeSVG value={loginUrl} size={200} level="M" data-testid="install-qr" />
              </div>
              <p className="text-xs text-[#64748B] text-center">
                Pointez l&apos;appareil photo de votre téléphone vers ce QR code.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase font-medium text-[#64748B]">
                  Lien direct de l&apos;application
                </label>
                <div
                  className="mt-1 p-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded font-mono text-xs break-all select-all"
                  data-testid="install-link"
                >
                  {loginUrl}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button onClick={copy} variant="outline" data-testid="install-copy-btn">
                  {copied ? <Check size={14} className="mr-2 text-green-600" /> : <Copy size={14} className="mr-2" />}
                  {copied ? 'Copié !' : 'Copier'}
                </Button>
                <Button onClick={share} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="install-share-btn">
                  <ShareNetwork size={14} className="mr-2" />
                  Partager
                </Button>
              </div>

              <Button
                onClick={() => (window.location.href = loginUrl)}
                variant="outline"
                className="w-full border-[#10B981] text-[#10B981] hover:bg-[#DCFCE7]"
                data-testid="install-open-btn"
              >
                <DownloadSimple size={14} className="mr-2" />
                Ouvrir l&apos;application maintenant
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Platform tabs */}
        <Card className="border-0 shadow-xl" data-testid="install-instructions-card">
          <CardContent className="p-6">
            <h2 className="font-outfit text-lg font-bold text-[#0F172A] mb-3">
              Comment installer sur mon téléphone ?
            </h2>

            <div className="grid grid-cols-3 gap-2 mb-5">
              {PLATFORMS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPlatform(p.key)}
                  data-testid={`install-tab-${p.key}`}
                  className={`p-3 rounded-lg border text-sm font-medium transition-all flex flex-col items-center gap-1 ${
                    platform === p.key
                      ? 'border-[#0052FF] bg-[#EFF6FF] text-[#0052FF]'
                      : 'border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]'
                  }`}
                >
                  <p.icon size={22} weight="duotone" style={{ color: platform === p.key ? '#0052FF' : p.color }} />
                  <span>{p.label}</span>
                </button>
              ))}
            </div>

            <InstallInstructions platform={platform} />

            {platform === 'ios' && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                ⚠️ Sur iPhone, l&apos;installation n&apos;est possible qu&apos;avec Safari.
                Depuis Chrome iOS, l&apos;option d&apos;ajout à l&apos;écran d&apos;accueil est masquée.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Access details */}
        <Card className="border-0 shadow-xl bg-white/95 backdrop-blur">
          <CardContent className="p-6">
            <h2 className="font-outfit text-lg font-bold text-[#0F172A] mb-3">
              Comment se connecter ?
            </h2>
            <ul className="space-y-2 text-sm text-[#334155]">
              <li className="flex gap-2">
                <Check size={16} className="text-[#10B981] shrink-0 mt-0.5" weight="bold" />
                <span>Choisissez votre profil : <b>Enseignant</b> ou <b>Personnel</b>.</span>
              </li>
              <li className="flex gap-2">
                <Check size={16} className="text-[#10B981] shrink-0 mt-0.5" weight="bold" />
                <span>Saisissez votre <b>ID</b> et votre <b>code PIN</b> (6 chiffres pour les enseignants,
                  4 chiffres pour le personnel Cantine/Transport).</span>
              </li>
              <li className="flex gap-2">
                <Check size={16} className="text-[#10B981] shrink-0 mt-0.5" weight="bold" />
                <span>Le PIN est communiqué par <b>l&apos;administration de l&apos;école</b>.
                  Un QR code peut vous être fourni pour une connexion en un clic.</span>
              </li>
            </ul>

            <div className="mt-4 pt-4 border-t border-[#E2E8F0] text-center">
              <a href="/login" className="text-xs text-[#0052FF] hover:underline" data-testid="install-back-web">
                ← Retour au portail web administrateur
              </a>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-xs text-white/80 pb-4">
          Aucun store à télécharger — l&apos;application s&apos;installe directement depuis ce lien.
        </div>
      </div>
    </div>
  );
};

export default MobileInstall;
